import { fft } from 'fftjs'
import type { FFTSignal } from './types.js'
import { findFFTBin } from './fft.js'

export function isPowerOfTwo(num: number) {
  while (num % 2 === 0 && num > 1) {
    num /= 2
  }

  return num === 1
}

export function error(message: unknown) {
  throw new Error('Error: ' + message)
}

export function pointwiseBufferMult(a: Float32Array, b: Float32Array) {
  const c: number[] = []
  for (let i = 0; i < Math.min(a.length, b.length); i++) {
    c[i] = a[i] * b[i]
  }

  return c
}

export function createBarkScale(length: number, sampleRate: number, bufferSize: number): Float32Array {
  const barkScale = new Float32Array(length)

  for (let i = 0; i < barkScale.length; i++) {
    barkScale[i] = (i * sampleRate) / bufferSize
    barkScale[i] = 13 * Math.atan(barkScale[i] / 1315.8) + 3.5 * Math.atan(Math.pow(barkScale[i] / 7518, 2))
  }

  return barkScale
}

export function typedToArray(t: Float32Array) {
  // utility to convert typed arrays to normal arrays
  return Array.prototype.slice.call(t)
}

export function arrayToTyped(t: number[]) {
  // utility to convert arrays to typed F32 arrays
  return Float32Array.from(t)
}

// eslint-disable-next-line no-underscore-dangle
export function _normalize(num: number, range: number) {
  return num / range
}

export function normalize(a: Float32Array, range: number) {
  return a.map(n => _normalize(n, range))
}

export function normalizeToOne(a: number[]) {
  const max = Math.max.apply(null, a)

  return a.map(n => n / max)
}

export function mean(a: Float32Array) {
  return a.reduce((prev, cur) => prev + cur) / a.length
}

/** Root mean square of a signal, linear. */
export const rms = (signal: Float32Array) => {
  let sum = 0
  for (const sample of signal) {
    sum += sample * sample
  }
  return Math.sqrt(sum / signal.length)
}

// eslint-disable-next-line no-underscore-dangle
function _melToFreq(melValue: number) {
  const freqValue = 700 * (Math.exp(melValue / 1125) - 1)
  return freqValue
}

// eslint-disable-next-line no-underscore-dangle
function _freqToMel(freqValue: number) {
  const melValue = 1125 * Math.log(1 + freqValue / 700)
  return melValue
}

export function melToFreq(mV: number) {
  return _melToFreq(mV)
}

export function freqToMel(fV: number) {
  return _freqToMel(fV)
}

export function createMelFilterBank(numFilters: number, sampleRate: number, bufferSize: number): number[][] {
  // the +2 is the upper and lower limits
  const melValues = new Float32Array(numFilters + 2)
  const melValuesInFreq = new Float32Array(numFilters + 2)

  // Generate limits in Hz - from 0 to the nyquist.
  const lowerLimitFreq = 0
  const upperLimitFreq = sampleRate / 2

  // Convert the limits to Mel
  const lowerLimitMel = _freqToMel(lowerLimitFreq)
  const upperLimitMel = _freqToMel(upperLimitFreq)

  // Find the range
  const range = upperLimitMel - lowerLimitMel

  // Find the range as part of the linear interpolation
  const valueToAdd = range / (numFilters + 1)

  const fftBinsOfFreq: number[] = new Array(numFilters + 2)

  for (let i = 0; i < melValues.length; i++) {
    // Initialising the mel frequencies
    // They're a linear interpolation between the lower and upper limits.
    melValues[i] = i * valueToAdd

    // Convert back to Hz
    melValuesInFreq[i] = _melToFreq(melValues[i])

    // Find the corresponding bins
    fftBinsOfFreq[i] = Math.floor(((bufferSize + 1) * melValuesInFreq[i]) / sampleRate)
  }

  const filterBank: number[][] = new Array(numFilters)
  for (let j = 0; j < filterBank.length; j++) {
    // Create a two dimensional array of size numFilters * (buffersize/2)+1
    // pre-populating the arrays with 0s.
    filterBank[j] = new Array(bufferSize / 2 + 1).fill(0)

    // creating the lower and upper slopes for each bin
    for (let i = fftBinsOfFreq[j]; i < fftBinsOfFreq[j + 1]; i++) {
      filterBank[j][i] = (i - fftBinsOfFreq[j]) / (fftBinsOfFreq[j + 1] - fftBinsOfFreq[j])
    }

    for (let i = fftBinsOfFreq[j + 1]; i < fftBinsOfFreq[j + 2]; i++) {
      filterBank[j][i] = (fftBinsOfFreq[j + 2] - i) / (fftBinsOfFreq[j + 2] - fftBinsOfFreq[j + 1])
    }
  }

  return filterBank
}

export function hzToOctaves(freq: number, A440: number) {
  return Math.log2((16 * freq) / A440)
}

export function normalizeByColumn(a: number[][]) {
  const emptyRow = a[0].map(() => 0)
  const colDenominators = a
    .reduce((acc, row) => {
      row.forEach((cell, j) => {
        acc[j] += Math.pow(cell, 2)
      })
      return acc
    }, emptyRow)
    .map(Math.sqrt)
  return a.map(row => row.map((v, j) => v / (colDenominators[j] || 1)))
}

export function frame({
  buffer,
  frameLength,
  hopLength,
}: {
  buffer: Float32Array
  frameLength: number
  hopLength: number
}) {
  if (buffer.length < frameLength) {
    throw new Error('Buffer is too short for frame length')
  }
  if (hopLength < 1) {
    throw new Error('Hop length cannot be less that 1')
  }
  if (frameLength < 1) {
    throw new Error('Frame length cannot be less that 1')
  }

  const numFrames = 1 + Math.floor((buffer.length - frameLength) / hopLength)

  return new Array(numFrames).fill(0).map((_, i) => buffer.slice(i * hopLength, i * hopLength + frameLength))
}

export function applyWindow(signal: Float32Array, window: Float32Array) {
  return pointwiseBufferMult(signal, window)
}

/** This function processes the signal with the windowing function and returns the FFT and amplitude-spectrum  */
export const prepareSignalWithSpectrum = ({
  signal,
  window,
  bufferSize,
}: {
  signal: Float32Array
  window: Float32Array
  bufferSize: number
}): FFTSignal => {
  const windowedSignal = applyWindow(signal, window)
  const complexSpectrum = fft(windowedSignal)
  const ampSpectrum = new Float32Array(bufferSize / 2)
  for (let i = 0; i < bufferSize / 2; i++) {
    ampSpectrum[i] = Math.sqrt(Math.pow(complexSpectrum.real[i], 2) + Math.pow(complexSpectrum.imag[i], 2))
  }

  return {
    signal,
    ampSpectrum,
    ampSpectrumDB: ampSpectrum.map(v => 20 * Math.log10(v)),
    chroma: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  }
}

/** Normalize AnalyserNode DB values to 0...1 */
export const normalizeAmpDB = ({ ampSpectrumDB }: { ampSpectrumDB: Float32Array }) => {
  const ampSpectrum = new Float32Array(ampSpectrumDB.length)

  // We normalize between 0..1, 0 being -100 and below, and 1 being -30 and above
  for (let i = 0; i < ampSpectrumDB.length; i++) {
    const val = ampSpectrumDB[i]
    if (val <= -100) {
      ampSpectrum[i] = 0
    } else if (val >= -30) {
      ampSpectrum[i] = 1
    } else {
      const maxVal = 70 // 70 dB range between -100 and -30
      const rangeVal = val + 100

      ampSpectrum[i] = rangeVal / maxVal
    }
  }
  return ampSpectrum
}

export const generateSinewave = ({
  length = 4096,
  frequency = 440,
  sampleRate,
}: {
  frequency?: number
  length?: number
  sampleRate: number
}) => {
  const samples = new Float32Array(length)

  for (let i = 0; i < length; i++) {
    samples[i] = Math.sin((2 * Math.PI * frequency * i) / sampleRate)
  }

  return samples
}

export const generateSineChord = ({
  sampleRate,
  length = 4096,
  frequencies,
}: {
  sampleRate: number
  length?: number
  frequencies: number[]
}) => {
  const waves = frequencies.map(f => generateSinewave({ length, frequency: f, sampleRate }))

  // Merge the waves, normalizing the amplitude
  const mergedWave = new Float32Array(length)
  for (let i = 0; i < length; i++) {
    const values = waves.map(w => w[i])
    const sum = values.reduce((prev, cur) => prev + cur, 0)
    mergedWave[i] = sum / values.length
  }
  return mergedWave
}

/** Generate a fake FFT array  */
export const generateFFTData = ({
  fftSize = 4096,
  sampleRate = 44100,
  fundamentals,
  harmonics = 5,
}: {
  sampleRate: number
  fftSize: number
  fundamentals: number[]
  harmonics?: number
}) => {
  const numBins = fftSize / 2
  // const binWidth = sampleRate / (2 * numBins) // Hz per bin

  // Initialize FFT data array
  const fftArray = new Float32Array(numBins).fill(0)

  // Generate harmonics for each fundamental frequency
  for (const fundamental of fundamentals) {
    for (let harmonic = 1; harmonic <= harmonics; harmonic++) {
      const freq = fundamental * harmonic
      const binIndex = findFFTBin({ fftSize, sampleRate, frequency: freq })

      if (binIndex < numBins) {
        // Amplitude decreases with harmonic number
        const amplitude = 1.0 / harmonic
        fftArray[binIndex] += amplitude
      }
    }
  }

  return fftArray
}
